import { useMemo } from 'react';
import { useParams } from 'next/navigation';
import { processText } from '@/shared/libs/process-text';
import {
  normalizeGameIds,
  useGamesByIds,
} from '@/entities/game/model/api';
import type { GameCatalogItem } from '@/entities/game/model/types';

interface GameIdentificationResult {
  currentGameData: GameCatalogItem | null;
  isActiveGame: boolean | null;
}

interface GameIdentificationInput {
  gameId?: string | number | null;
}

const getRouteParam = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value[0] : value;

export const useGameIdentification = (
  goldCard?: GameIdentificationInput
): GameIdentificationResult => {
  const params = useParams();
  const titleGame = getRouteParam(params?.titleGame);
  const categories = getRouteParam(params?.categories);
  const gameId = useMemo(
    () => normalizeGameIds([goldCard?.gameId])[0] ?? null,
    [goldCard?.gameId]
  );
  const shouldLoadGame = Boolean(gameId && titleGame && categories);
  const { games } = useGamesByIds(shouldLoadGame ? [gameId] : []);

  const currentGameData = useMemo(() => {
    if (!gameId) return null;
    return games.find((game) => game.id === gameId) ?? null;
  }, [gameId, games]);


  const isActiveGame = useMemo(() => {
    if (!currentGameData || !titleGame || !categories) return null;
    
    return (
      (currentGameData.code || processText(currentGameData.title)) ===
        processText(titleGame) && currentGameData.providerId === categories
    );
  }, [categories, currentGameData, titleGame]);

  return {
    currentGameData,
    isActiveGame,
  };
};
